// Public privacy policy. Mirrors the layout convention used by
// FollowJourneyPage: max-width 720px container, dark theme via CSS
// variables with fallbacks, Hanken Grotesk + Fraunces fonts. Plain
// anchors for navigation — App.jsx routes by full page reload.

const SUBPROCESSORS = [
  ['Supabase',         'Database, authentication, and file storage',     'USA'],
  ['OpenAI',           'AI-assisted lesson content generation',          'USA'],
  ['Stripe',           'Payment processing and subscription management', 'USA'],
  ['Vercel',           'Application hosting and serverless functions',   'USA'],
  ['Twilio',           'SMS delivery (opt-in only)',                     'USA'],
  ['Google (YouTube)', 'Video content for lesson supplements',           'USA'],
  ['Adzuna',           'Job market data in learning context',            'UK / USA'],
];

export default function PrivacyPage() {
  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: 'var(--bg-primary, #0f0f12)',
      color: 'var(--text-primary, #e8e6e1)',
      fontFamily: "'Hanken Grotesk', 'Inter', system-ui, sans-serif",
    },
    container: {
      maxWidth: '720px',
      margin: '0 auto',
      padding: '40px 24px 96px',
    },
    backLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      color: 'var(--text-secondary, #a8a49e)',
      fontSize: '14px',
      fontWeight: '500',
      textDecoration: 'none',
      marginBottom: '40px',
      transition: 'color 0.15s',
    },
    title: {
      fontFamily: "'Fraunces', serif",
      fontSize: 'clamp(32px, 4.8vw, 44px)',
      fontWeight: '500',
      lineHeight: '1.1',
      letterSpacing: '-0.02em',
      margin: '0 0 12px',
    },
    effective: {
      fontSize: '14px',
      color: 'var(--text-muted, #8f8b82)',
      marginBottom: '48px',
    },
    section: {
      marginBottom: '40px',
    },
    h2: {
      fontFamily: "'Fraunces', serif",
      fontSize: '22px',
      fontWeight: '500',
      lineHeight: '1.3',
      color: 'var(--text-primary, #e8e6e1)',
      margin: '0 0 16px',
    },
    h3: {
      fontSize: '14px',
      fontWeight: '700',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: 'var(--accent-primary, #2dd4bf)',
      margin: '24px 0 12px',
    },
    p: {
      fontSize: '15px',
      lineHeight: '1.75',
      color: 'var(--text-secondary, #a8a49e)',
      margin: '0 0 14px',
    },
    ul: {
      paddingLeft: '20px',
      margin: '0 0 14px',
      color: 'var(--text-secondary, #a8a49e)',
      fontSize: '15px',
      lineHeight: '1.75',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      margin: '16px 0 20px',
      fontSize: '14px',
      background: 'var(--bg-secondary, #1a1a21)',
      border: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
      borderRadius: '8px',
      overflow: 'hidden',
    },
    th: {
      textAlign: 'left',
      padding: '12px 16px',
      borderBottom: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
      color: 'var(--text-primary, #e8e6e1)',
      fontWeight: '700',
      fontSize: '12px',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    },
    td: {
      padding: '12px 16px',
      borderBottom: '1px solid var(--border-soft, rgba(255,255,255,0.04))',
      color: 'var(--text-secondary, #a8a49e)',
      verticalAlign: 'top',
    },
    contactBlock: {
      marginTop: '12px',
      padding: '16px 20px',
      background: 'var(--bg-secondary, #1a1a21)',
      border: '1px solid var(--border-soft, rgba(255,255,255,0.06))',
      borderRadius: '8px',
      color: 'var(--text-secondary, #a8a49e)',
      fontSize: '15px',
      lineHeight: '1.7',
    },
    link: {
      color: 'var(--accent-primary, #2dd4bf)',
      textDecoration: 'none',
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <a href="/" style={styles.backLink}>← Back to AdaptOS</a>

        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.effective}>Effective date: June 1, 2026</p>

        <section style={styles.section}>
          <h2 style={styles.h2}>1. Who We Are</h2>
          <p style={styles.p}>
            AdaptOS, Inc. (&ldquo;AdaptOS,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is an adaptive
            IT certification learning platform at adaptos.app, incorporated in Delaware
            and headquartered in Fort Worth, Texas. Contact us at <a href="mailto:Info@AdaptOS.App" style={styles.link}>Info@AdaptOS.App</a>.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>2. Information We Collect</h2>

          <h3 style={styles.h3}>Information You Provide Directly</h3>
          <ul style={styles.ul}>
            <li>Name and email address (account registration)</li>
            <li>Phone number (optional — SMS study reminders, only if you opt in)</li>
            <li>SMS consent and consent timestamp</li>
            <li>Age confirmation (you must be 13 or older)</li>
            <li>Intake survey responses (certification goals, IT background, study schedule)</li>
            <li>Payment information (processed by Stripe — we never store card numbers)</li>
            <li>Affiliate referral code (if you are an affiliate applicant)</li>
          </ul>

          <h3 style={styles.h3}>Information Generated by Platform Use</h3>
          <ul style={styles.ul}>
            <li>Lesson progress and completion records</li>
            <li>Quiz attempt history (scores, incorrect answers, remediation paths)</li>
            <li>Mastery and confidence scores per topic</li>
            <li>Knowledge gap data (concepts flagged for review)</li>
            <li>Session activity and engagement patterns</li>
            <li>Study streaks</li>
            <li>Conversation summaries (compressed records of AI-assisted lesson turns — not full transcripts)</li>
          </ul>

          <h3 style={styles.h3}>Information Collected Automatically</h3>
          <ul style={styles.ul}>
            <li>IP address (hashed — not stored in raw form)</li>
            <li>Browser type and operating system</li>
            <li>Referring URL and UTM parameters</li>
            <li>Page views and navigation events</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>3. How We Use Your Information</h2>
          <p style={styles.p}>We use your information to:</p>
          <ul style={styles.ul}>
            <li>Deliver, personalize, and improve the AdaptOS learning experience</li>
            <li>Track mastery progress and adapt lesson sequencing and pacing</li>
            <li>Send transactional emails (account confirmation, password reset, receipts)</li>
            <li>Send SMS study reminders (only if you have opted in)</li>
            <li>Process payments and manage your subscription via Stripe</li>
            <li>Detect and prevent fraud and abuse</li>
            <li>Comply with legal obligations</li>
            <li>Communicate platform updates and new certification tracks</li>
          </ul>
          <p style={styles.p}>
            We do not use your information to serve third-party advertising. AdaptOS is ad-free.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>4. How We Share Your Information</h2>
          <p style={styles.p}>
            We do not sell your personal information. We share data only with the following
            service providers to operate the platform:
          </p>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Subprocessor</th>
                <th style={styles.th}>Purpose</th>
                <th style={styles.th}>Location</th>
              </tr>
            </thead>
            <tbody>
              {SUBPROCESSORS.map(([name, purpose, location]) => (
                <tr key={name}>
                  <td style={styles.td}>{name}</td>
                  <td style={styles.td}>{purpose}</td>
                  <td style={styles.td}>{location}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={styles.p}>
            Each subprocessor is contractually bound to use your data only for the stated
            purpose. We may disclose information if required by law or to protect the safety
            of our users or the public.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>5. Data Retention</h2>
          <p style={styles.p}>
            We retain account data and learning progress while your account is active. Upon
            account deletion, personal information is deleted within 30 days, except billing
            records required for tax compliance (retained 7 years). Hashed IP addresses used
            for rate-limiting are retained for 90 days.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>6. Your Rights</h2>
          <p style={styles.p}>You may have the following rights depending on your location:</p>
          <ul style={styles.ul}>
            <li><strong>Access:</strong> request a copy of the data we hold about you</li>
            <li><strong>Correction:</strong> request correction of inaccurate data</li>
            <li><strong>Deletion:</strong> request deletion of your account and personal data</li>
            <li><strong>Portability:</strong> request your learning data in a machine-readable format</li>
            <li><strong>Opt-out of SMS:</strong> reply STOP to any SMS, or email <a href="mailto:Info@AdaptOS.App" style={styles.link}>Info@AdaptOS.App</a></li>
          </ul>
          <p style={styles.p}>
            <strong>California residents (CCPA):</strong> you have the right to know, delete,
            and opt out of the sale of personal information. We do not sell personal information.
          </p>
          <p style={styles.p}>
            <strong>EEA/UK residents (GDPR/UK GDPR):</strong> you have the right to object to
            processing, restrict processing, and lodge a complaint with your local supervisory
            authority.
          </p>
          <p style={styles.p}>
            To exercise any right, email <a href="mailto:Info@AdaptOS.App" style={styles.link}>Info@AdaptOS.App</a>. We will respond within 30 days.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>7. Cookies and Tracking</h2>
          <p style={styles.p}>
            AdaptOS uses cookies to maintain your authenticated session, remember preferences,
            and measure platform usage (first-party only). We do not use third-party advertising
            cookies.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>8. Children&rsquo;s Privacy</h2>
          <p style={styles.p}>
            AdaptOS is not directed to children under 13. We do not knowingly collect data
            from anyone under 13. Contact <a href="mailto:Info@AdaptOS.App" style={styles.link}>Info@AdaptOS.App</a> if you believe this has occurred.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>9. Security</h2>
          <p style={styles.p}>
            Security measures include: TLS 1.2+ encryption in transit; encryption at rest;
            row-level security on all learner data; JWT-based authentication with short-lived
            tokens; hashed IP storage. In the event of a breach affecting your rights, we
            will notify you and relevant authorities as required by law.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>10. Changes to This Policy</h2>
          <p style={styles.p}>
            We will notify you of material changes by email or platform notice. Continued use
            after the effective date constitutes acceptance.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>11. Contact</h2>
          <div style={styles.contactBlock}>
            AdaptOS, Inc.<br />
            Fort Worth, Texas<br />
            <a href="mailto:Info@AdaptOS.App" style={styles.link}>Info@AdaptOS.App</a>
          </div>
        </section>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300..600&family=Hanken+Grotesk:wght@300;400;500;600;700&display=swap');
        a:hover { opacity: 0.85; }
      `}</style>
    </div>
  );
}
